<script setup>
import { computed, defineProps, onMounted, onUnmounted, ref } from 'vue';
import { fetchData } from '../../fetchData.js';
import Path from './Path.vue';

const props = defineProps({
    toOverview: {
        type: String,
        required: false,
        default: '#'
    },
    currentProductGroup: {
        type: Object,
        required: true,
        default: () => ({
            name: '',
            image: '',
            products: {
                data: []
            }
        })
    }
});
const API = `/api/v2/contentstreamList?sort[by]=published_at&page_size=1&sort[order]=desc`;
const currentProductGroupName = props.currentProductGroup.name;
const currentProductImage = props.currentProductGroup.image;
const currentProducts = props.currentProductGroup.products.data;

const contentStreamTypes = ref([]);
const lastIndicatorAnaylyses = ref(null);
const lastAnalyses = ref(null);
const lastVideoAnalyses = ref(null);
const lastShortAnalyses = ref(null);
const stickyTabBar = ref(false);
const localHeader = ref(null);
const isDataLoaded = ref(false);

const getMenuTabs = computed(() => {
    if (!isDataLoaded.value) {
        return [{ label: 'overview', action: props.toOverview, condition: true }];
    }

    const analyses = [
        { label: 'plain_analysis', data: lastAnalyses.value, route: '/dashboard/markets-analysis/' },
        { label: 'contenstream_category_short', data: lastShortAnalyses.value, route: '/dashboard/markets-analysis/' },
        { label: 'analysis_video', data: lastVideoAnalyses.value, route: '/dashboard/markets-videoanalysis/' },
        { label: 'indicator_analysis', data: lastIndicatorAnaylyses.value, route: '/dashboard/markets-analysis/' }
    ];

    return [
        { label: 'overview', action: props.toOverview, condition: true },
        ...analyses
            .filter(({ data }) => data?.id)
            .map(({ label, data, route }) => ({
                label,
                action: `${route}${data.id}`,
                condition: true
            }))
    ];
});

const getIndicatorAnalyses = async () => {
    let url = `${API}&indicator_analysis=true`;

    if (!currentProducts[0]?.id) {
        return;
    }

    url += `&product_id[]=${currentProducts[0].id}`;

    try {
        const { result = [] } = await fetchData(url);
        lastIndicatorAnaylyses.value = result.data && result.data.length > 0 ? result.data[0] : null;
    } catch (error) {
        console.error('Error fetching indicator analyses:', error);
        lastIndicatorAnaylyses.value = null;
    }
};
const getShortAnalyses = async () => {
    let url = `${API}`;

    if (!currentProducts[0]?.id) {
        return;
    }

    url += `&product_id[]=${currentProducts[0].id}`;

    const short = contentStreamTypes.value.filter((type) => type.name === 'short');

    if (short.length !== 0) {
        url += `&contentstream_type_id[]=${short[0].id}`;
    }

    try {
        const { result = [] } = await fetchData(url);
        lastShortAnalyses.value = result.data && result.data.length > 0 ? result.data[0] : null;
    } catch (error) {
        console.error('Error fetching short analyses:', error);
        lastShortAnalyses.value = null;
    }
};
const getLastAnalyses = async () => {
    let url = `${API}&indicator_analysis=false`;

    if (!currentProducts[0]?.id) {
        return;
    }

    url += `&product_id[]=${currentProducts[0].id}`;

    const relevantTypes = contentStreamTypes.value.filter((type) => ['wmu', 'dmu', 'dsu'].includes(type.name));

    if (relevantTypes.length > 0) {
        const typeIds = relevantTypes.map((type) => `&contentstream_type_id[]=${type.id}`).join('');
        url += typeIds;
    }

    try {
        const { result = [] } = await fetchData(url);
        lastAnalyses.value = result.data && result.data.length > 0 ? result.data[0] : null;
    } catch (error) {
        console.error('Error fetching last analyses:', error);
        lastAnalyses.value = null;
    }
};
const getVideoAnalyses = async () => {
    let url = `${API}`;

    if (!currentProducts[1]?.id) {
        return;
    }

    url += `&product_id[]=${currentProducts[1].id}`;

    const webinar = contentStreamTypes.value.filter((type) => type.name === 'webinar');

    if (webinar.length !== 0) {
        url += `&contentstream_type_id[]=${webinar[0].id}`;
    }

    try {
        const { result = [] } = await fetchData(url);
        lastVideoAnalyses.value = result.data && result.data.length > 0 ? result.data[0] : null;
    } catch (error) {
        console.error('Error fetching video analyses:', error);
        lastVideoAnalyses.value = null;
    }
};

const handleScroll = () => {
    const tabBar = localHeader.value;

    if (tabBar) {
        stickyTabBar.value = true;
    }
};

const getContentSteamTypes = async () => {
    try {
        const url = '/api/v1/contentstream-types';
        const { result = [] } = await fetchData(url);
        contentStreamTypes.value = result.data;
    } catch (error) {
        console.error('Error fetching data:', error);
    }
};
onMounted(async () => {
    window.addEventListener('scroll', handleScroll);

    await getContentSteamTypes();
    await getLastAnalyses();
    await getIndicatorAnalyses();
    await getShortAnalyses();
    await getVideoAnalyses();
    isDataLoaded.value = true;
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
defineExpose({
    localHeader
});
</script>

<template>
    <Path v-if="currentProductGroupName">
        <router-link to="/dashboard" class="text-blue-500">Dashboard</router-link>
        <i class="flex fi fi-rr-angle-small-right"></i>
        <router-link to="/dashboard/markets" class="text-blue-500">{{ $t('plain_analysis') }}</router-link>
        <i class="flex fi fi-rr-angle-small-right"></i>
        <p class="text-blue-500">{{ $translate(currentProductGroupName) }}</p>
    </Path>

    <div ref="localHeader" :class="['flex flex-col sm:flex-row justify-between py-2 gap-2 border-gray-300', stickyTabBar && 'sticky top-[80px] z-50 w-full']">
        <div :class="['flex flex-row-reverse sm:flex-row items-center gap-4 transition-opacity', stickyTabBar && 'sm:opacity-0 hidden']">
            <div v-if="currentProductImage" class="p-3 bg-white rounded-xl">
                <img class="w-16 sm:w-24" :src="currentProductImage.replace('.png', '-gradient.png')" />
            </div>
            <div v-if="currentProductGroupName" class="flex flex-col w-full justify-between">
                <div class="flex flex-row justify-between items-center gap-2">
                    <p class="font-bold">{{ $translate(currentProductGroupName) }}</p>
                </div>
                <router-link to="/dashboard/markets" class="text-dashboard-primary-grey text-sm flex flex-row gap-2 items-center">
                    <i class="fi fi-rr-arrow-left flex text-dashboard-primary-grey"></i>
                    <p class="text-dashboard-primary-grey">{{ $t('navigate_to_markets') }}</p>
                </router-link>
            </div>
        </div>

        <div class="flex-1">
            <ul class="flex flex-1 items-center justify-center lg:justify-end">
                <div :class="['flex flex-wrap p-2 px-6 rounded-xl bg-white shadow-lg']">
                    <template v-for="(tab, index) in getMenuTabs" :key="index">
                        <li v-if="tab.condition" class="transition duration-400 cursor-pointer lg:font-bold inline-block p-1 lg:p-2 border-b-2 text-xs sm:text-center">
                            <router-link :to="tab.action" class="hover:bg-blue/70 hover:text-white p-1 lg:p-2 rounded-lg flex items-center relative">
                                <div class="flex gap-2">
                                    <span class="flex-1">{{ $t(tab.label) }}</span>

                                    <span v-if="tab.label === 'name_IT'" class="text-xs text-white bg-blue px-1 rounded-md font-semibold">
                                        {{ $t('plain_new') }}
                                    </span>
                                </div>
                            </router-link>
                        </li>
                    </template>
                </div>
            </ul>
        </div>
    </div>
</template>
